/**
 * Les montants circulent en centimes entiers : les flottants accumulent
 * l'erreur d'arrondi (0,1 + 0,2 ≠ 0,3). La conversion en euros n'a lieu qu'ici,
 * au moment de l'affichage, et dans la saisie du formulaire.
 */
export function formaterMontant(centimes, devise = 'EUR') {
  if (centimes === null || centimes === undefined) return null;
  // Les centimes ne sont affichés que s'il y en a : « 1 200 € » pour un compte
  // rond, « 1 200,50 € » sinon. Arrondir systématiquement à l'euro afficherait
  // 1 201 € pour une saisie de 1 200,50 €, en contradiction avec ce que
  // l'utilisateur a tapé et avec ce qui est enregistré.
  const decimales = centimes % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: devise,
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(centimes / 100);
}

/** Centimes → chaîne à saisir en euros, et inversement. */
export const centimesVersEuros = (centimes) =>
  centimes === null || centimes === undefined ? '' : String(centimes / 100);

export function eurosVersCentimes(saisie) {
  const nettoye = String(saisie).replace(',', '.').trim();
  if (nettoye === '') return null;
  const euros = Number(nettoye);
  if (!Number.isFinite(euros)) return null;
  // Arrondi explicite : 12,34 € vaut 1234 centimes, sans reliquat flottant.
  return Math.round(euros * 100);
}

const dateCourte = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export const formaterDate = (iso) => (iso ? dateCourte.format(new Date(iso)) : null);

/** Date ISO → « AAAA-MM-JJ », format attendu par un champ de type date. */
export const versChampDate = (iso) => (iso ? iso.slice(0, 10) : '');

/** Résumé des dates ou de la durée, selon le mode choisi. */
export function resumerPeriode(voyage) {
  if (voyage.dateMode === 'fixed_dates' && voyage.startDate) {
    // L'heure, quand elle est connue, se colle à sa date : « du 4 septembre
    // 2027 à 18:00 au 6 septembre 2027 à 09:30 ». Elle reste au format du
    // champ de saisie, le même que celui des horaires de l'itinéraire.
    const debut = [formaterDate(voyage.startDate), voyage.startTime].filter(Boolean).join(' à ');
    const fin = [formaterDate(voyage.endDate), voyage.endTime].filter(Boolean).join(' à ');
    return `Du ${debut} au ${fin}`;
  }
  const duree = voyage.durationDays ? `${voyage.durationDays} jours` : null;
  return [duree, voyage.period].filter(Boolean).join(' · ') || 'Dates à définir';
}

/**
 * Décrit un participant en une ligne : « Léa, 7 ans — sans arachide ».
 * Chaque partie est facultative, on ne connaît parfois que la contrainte.
 */
export function decrireParticipant(participant, index) {
  const identite = [
    participant.name || `Participant ${index + 1}`,
    participant.age !== null && participant.age !== undefined ? `${participant.age} ans` : null,
  ]
    .filter(Boolean)
    .join(', ');

  return participant.restrictions.length > 0
    ? `${identite} — ${participant.restrictions.join(', ')}`
    : identite;
}

const moisCourt = new Intl.DateTimeFormat('fr-FR', { month: 'short' });

/**
 * Dates en une ligne, pour les cartes : « 12–17 juin 2027 ».
 *
 * Le format long — « Du 12 juin 2027 au 17 juin 2027 » — occupait deux lignes
 * sur une carte et répétait le mois et l'année sans rien apprendre.
 */
export function resumerPeriodeCourte(voyage) {
  if (voyage.dateMode === 'fixed_dates' && voyage.startDate) {
    const debut = new Date(voyage.startDate);
    const fin = new Date(voyage.endDate);
    const memeAnnee = debut.getFullYear() === fin.getFullYear();
    const memeMois = memeAnnee && debut.getMonth() === fin.getMonth();

    if (memeMois) {
      return `${debut.getDate()}–${fin.getDate()} ${moisCourt.format(fin)} ${fin.getFullYear()}`;
    }
    if (memeAnnee) {
      return `${debut.getDate()} ${moisCourt.format(debut)} – `
        + `${fin.getDate()} ${moisCourt.format(fin)} ${fin.getFullYear()}`;
    }
    return `${debut.getDate()} ${moisCourt.format(debut)} ${debut.getFullYear()} – `
      + `${fin.getDate()} ${moisCourt.format(fin)} ${fin.getFullYear()}`;
  }

  const duree = voyage.durationDays ? `${voyage.durationDays} jours` : null;
  return [duree, voyage.period].filter(Boolean).join(' · ') || 'Dates à définir';
}

/**
 * Âge en années révolues à partir d'une date « AAAA-MM-JJ », ou `null` si la
 * date est absente, invalide ou invraisemblable.
 *
 * Sert uniquement à confirmer la saisie à l'écran — le serveur refait le
 * calcul pour ce qu'il enregistre. Le silence sur une date incomplète est
 * voulu : un champ date renvoie des valeurs intermédiaires pendant la frappe,
 * et afficher « 1925 ans » en cours de saisie n'aiderait personne.
 */
export function ageDepuisNaissance(jour) {
  if (!jour) return null;

  const nee = new Date(`${jour}T00:00:00.000Z`);
  if (Number.isNaN(nee.getTime())) return null;
  // Aller-retour : le moteur JavaScript ne refuse pas un 30 février, il le
  // reporte au 2 mars. Comparer la date relue à la saisie est le seul moyen
  // sûr de repérer un jour qui n'existe pas.
  if (nee.toISOString().slice(0, 10) !== jour) return null;

  const maintenant = new Date();
  const annees = maintenant.getUTCFullYear() - nee.getUTCFullYear();
  // Zéro quand les mois coïncident : on départage alors sur le quantième.
  const ecartDansLAnnee =
    maintenant.getUTCMonth() - nee.getUTCMonth() || maintenant.getUTCDate() - nee.getUTCDate();

  const age = ecartDansLAnnee < 0 ? annees - 1 : annees;
  return age >= 0 && age <= 120 ? age : null;
}
